import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSites } from '@/hooks/useSites'
import { useSubscriptions } from '@/hooks/useSubscriptions'
import SiteSelector from '@/components/subscriptions/SiteSelector'
import SubscriptionList from '@/components/subscriptions/SubscriptionList'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { AlertCircle, Bell } from 'lucide-react'

export default function HomePage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSiteId = searchParams.get('site')

  const handleSelectSite = useCallback(
    (siteId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (siteId) next.set('site', siteId)
          else next.delete('site')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { data: sites, isLoading: sitesLoading, error: sitesError } = useSites()

  const {
    data: subscriptions,
    isLoading: subsLoading,
    error: subsError,
  } = useSubscriptions(selectedSiteId)

  return (
    <div className="h-full flex flex-col">
      {sitesLoading ? (
        <div className="flex-1 flex flex-col justify-center items-center gap-3">
          <LoadingSpinner size="lg" />
          <p className="text-sm text-gray-500">{t('sites.loading')}</p>
        </div>
      ) : sitesError ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {sitesError.message}
        </div>
      ) : sites && sites.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
          <Bell className="w-12 h-12 mb-3 text-gray-300" />
          <p>{t('sites.noSites')}</p>
        </div>
      ) : sites ? (
        <div className="flex-1 min-h-0 bg-white border-t border-gray-200 flex overflow-hidden">
          <SiteSelector
            sites={sites}
            selectedSiteId={selectedSiteId}
            onSelectSite={handleSelectSite}
          />

          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {!selectedSiteId ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <Bell className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm">{t('subscriptions.selectSite')}</p>
              </div>
            ) : subsError ? (
              <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {subsError.message}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4">
                <SubscriptionList
                  subscriptions={subscriptions ?? []}
                  siteId={selectedSiteId}
                  isLoading={subsLoading}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
